import { z } from 'zod';

/** Accepted CFB state carried by the existing Sixync live-room profile. */
export const CFB_SIXYNC_SCHEMA_VERSION = 1 as const;
const id = z.string().min(1).max(240);
const timestamp = z.number().finite().nonnegative();

/** A domain is part of a timestamp's identity. Receipt time is never media PTS. */
export const CfbClockReferenceSchema = z.object({
    clockId: id,
    domain: z.enum([
        'api_receipt_epoch_ms', 'provider_event_epoch_ms', 'server_epoch_ms',
        'source_media_ms', 'source_audio_samples', 'renderer_presentation_ms',
        // Local preview time alone does not establish a published RTP mapping.
        'browser_video_element_ms',
        'browser_audio_samples',
    ]),
    epoch: id.optional(),
    sourceId: id.optional(),
    unitsPerSecond: z.number().finite().positive(),
}).strict().superRefine((clock, ctx) => {
    if (!['source_audio_samples', 'browser_audio_samples'].includes(clock.domain) && clock.unitsPerSecond !== 1000) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Millisecond clocks use 1000 units per second' });
    }
    if (['source_media_ms', 'source_audio_samples', 'renderer_presentation_ms', 'browser_video_element_ms', 'browser_audio_samples'].includes(clock.domain) && !clock.epoch) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Media and renderer clocks require an epoch' });
    }
});

/** Only measured mappings belong here; a game clock is a content feature. */
export const CfbClockMappingSchema = z.object({
    mappingId: id,
    version: z.number().int().nonnegative(),
    from: CfbClockReferenceSchema,
    to: CfbClockReferenceSchema,
    fromPosition: timestamp,
    toPosition: timestamp,
    rate: z.number().finite().positive(),
    uncertaintyMs: timestamp,
    measuredAt: timestamp,
    expiresAt: timestamp,
    evidenceIds: z.array(id).min(1).max(16),
}).strict().superRefine((mapping, ctx) => {
    if (mapping.expiresAt <= mapping.measuredAt) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Mapping must expire after its measurement' });
    }
});

/** No transcript, image, play description, score or speculative outcome is published. */
export const CfbSyncEvidenceReferenceSchema = z.object({
    evidenceId: id,
    modality: z.enum(['participant', 'api', 'vision', 'audio']),
    sourceId: id.optional(),
    sourceEpoch: id.optional(),
    observedAt: timestamp,
    receivedAt: timestamp.optional(),
    completedAt: timestamp.optional(),
    clock: CfbClockReferenceSchema.optional(),
    captureStart: timestamp.optional(),
    captureEnd: timestamp.optional(),
    uncertaintyMs: timestamp.optional(),
}).strict().superRefine((evidence, ctx) => {
    if ((evidence.captureStart !== undefined || evidence.captureEnd !== undefined) && !evidence.clock) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Capture positions require a clock reference' });
    }
    if (evidence.captureStart !== undefined && evidence.captureEnd !== undefined && evidence.captureEnd < evidence.captureStart) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Capture interval is reversed' });
    }
});

export const CfbPlaybackAnchorSchema = z.object({
    authority: z.enum(['participant', 'vision', 'audio', 'api']),
    clock: CfbClockReferenceSchema,
    positionMs: timestamp,
    acceptedAt: timestamp,
    /** Null means not measured; it does not mean an exact anchor. */
    uncertaintyMs: timestamp.nullable(),
    period: z.number().int().positive().optional(),
    gameClock: z.string().regex(/^\d{1,2}:[0-5]\d$/).optional(),
    evidenceIds: z.array(id).max(16),
}).strict().superRefine((anchor, ctx) => {
    if (anchor.clock.unitsPerSecond !== 1000) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Anchor positionMs requires a millisecond clock' });
    }
});

export const CfbPlaybackStateSchema = z.object({
    schemaVersion: z.literal(CFB_SIXYNC_SCHEMA_VERSION),
    watchSessionId: id,
    watchStartedAt: timestamp,
    gameId: z.number().int().positive(),
    generation: z.number().int().nonnegative(),
    mode: z.enum(['live', 'voice', 'screen']),
    status: z.enum(['ready', 'buffering', 'stale', 'awaiting_clock', 'awaiting_screen', 'stopped']),
    paused: z.boolean(),
    updatedAt: timestamp,
    source: z.object({ sourceId: id, epoch: id.optional() }).strict().optional(),
    anchor: CfbPlaybackAnchorSchema.optional(),
    /** Verified football content position, not a source/presentation clock mapping. */
    visiblePosition: z.object({
        period: z.number().int().min(1).max(4),
        gameClock: z.string().regex(/^\d{1,2}:[0-5]\d$/),
        observedAt: timestamp,
        evidenceIds: z.array(id).max(16),
    }).strict().optional(),
    /** Already eligible identity only; text and latest-feed facts stay private. */
    eligiblePlay: z.object({ playId: id, revision: id }).strict().optional(),
    evidence: z.array(CfbSyncEvidenceReferenceSchema).max(16),
    clockMappings: z.array(CfbClockMappingSchema).max(8),
    capabilities: z.object({
        rendererScheduling: z.literal(false),
        audioVisualMediaClock: z.literal(false),
    }).strict(),
}).strict();

/** Dispatch identity, not a renderer schedule or proof of audible playback. */
export const CfbNarrationCueSchema = z.object({
    schemaVersion: z.literal(CFB_SIXYNC_SCHEMA_VERSION),
    watchSessionId: id,
    gameId: z.number().int().positive(),
    generation: z.number().int().nonnegative(),
    requestId: id,
    audioId: id.optional(),
    playId: id,
    revision: id,
    dispatchedAt: timestamp,
    evidenceIds: z.array(id).max(16),
    delivery: z.literal('server_dispatch'),
}).strict();

export type CfbClockReference = z.infer<typeof CfbClockReferenceSchema>;
export type CfbClockMapping = z.infer<typeof CfbClockMappingSchema>;
export type CfbSyncEvidenceReference = z.infer<typeof CfbSyncEvidenceReferenceSchema>;
export type CfbPlaybackAnchor = z.infer<typeof CfbPlaybackAnchorSchema>;
export type CfbPlaybackState = z.infer<typeof CfbPlaybackStateSchema>;
export type CfbNarrationCue = z.infer<typeof CfbNarrationCueSchema>;
